/**
 * L'écran de saisie des heures réalisées — celui du chef d'équipe, du chef de chantier et du
 * conducteur d'engin.
 *
 * *Demande de Chris, 16/09/2026 : « le responsable devra saisir les heures pour lui-même et
 * toutes les personnes affectées sur les chapitres […] il doit avoir le moins de saisie possible
 * à faire, juste à valider les heures et pouvoir les corriger personne par personne si besoin ;
 * idem pour les ressources matériel de chantier affecté ; il devra aussi valider les tonnages
 * livrés […] les écrans doivent être responsive pour téléphone, tablettes et PC. »*
 *
 * ## Un seul écran pour les trois rôles
 *
 * Et non trois sous-menus. Le geste est identique — *je déclare ma journée et celle de mon
 * équipe* —, et ce qui change d'un rôle à l'autre est le contenu, que le compte suffit à
 * déduire. Trois entrées menant au même écran auraient obligé chacun à choisir son titre avant de
 * travailler, avec un écran vide en cas d'erreur.
 *
 * ## Tout est pré-rempli : le geste normal est UN bouton
 *
 * L'écran arrive avec les demi-journées que le plan place, à 4 h chacune, paniers cochés. Une
 * journée conforme se déclare d'un clic sur « Enregistrer ». Les corrections sont des
 * exceptions, et elles se font ligne par ligne sans jamais rouvrir le reste.
 *
 * C'est pourquoi **l'état de l'écran est initialisé avec les valeurs proposées**, et non vide : un
 * écran vide avec des « suggestions » grisées obligerait à un geste par ligne pour les accepter,
 * ce qui est exactement l'inverse de la demande.
 *
 * ## Le téléphone d'abord
 *
 * La saisie se fait le soir, souvent sur un téléphone, parfois au bord d'une piste. Sous 900 px,
 * chaque ressource devient une carte ; au-dessus, un tableau. Aucun geste ne dépend du survol.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { ajouterJours, formaterDate, formaterNombre } from "@planif/moteur";
import {
  adresseDuBon,
  chargerJourneeDeSaisie,
  defaireLaValidation,
  enregistrerLaSaisie,
  validerLaJournee,
} from "./api";
import type {
  ChantierDeSaisie,
  CircuitDeLaJournee,
  EtatDuMoment,
  JourneeDeSaisie,
  RessourceAjoutable,
  RessourceDeSaisie,
} from "./api";
import type { BaremePeriode, ZoneProposee, ZoneRetenue } from "@planif/moteur";
import { valeurDeLaZone } from "./valeurZone";

/** Ce qu'une demi-journée pèse : 4 h, partout dans le produit (PRD §4.4). */
const HEURES_PAR_DEMI_JOURNEE = "4";

const NOMS_ETAT: Record<EtatDuMoment, string> = {
  present: "présent",
  intemperie: "intempérie",
  absence: "absence",
  maladie: "maladie",
  formation: "formation",
};

const NOM_POSITION: Record<"matin" | "apres_midi", string> = {
  matin: "Matin",
  apres_midi: "Après-midi",
};

/**
 * Le nom d'un nœud, sans répéter son numéro.
 *
 * Les libellés d'ouvrage du DEPO **portent déjà leur numéro** — « 2.4 - ENROBE » sur la
 * production. Concaténer numéro et libellé écrivait « 2.4 2.4 - ENROBE », ce qui se lit comme une
 * coquille et fait douter du reste. Le préfixe n'est ajouté que lorsqu'il manque : rien ne
 * garantit que le classeur du client suivant écrira ses libellés de la même façon.
 */
export function nommerLeNoeud(noeud: { numero: string; libelle: string }): string {
  return noeud.libelle.startsWith(noeud.numero)
    ? noeud.libelle
    : `${noeud.numero} ${noeud.libelle}`;
}

/* ---------------------------------------------------------------------------
 * L'état de travail
 * ------------------------------------------------------------------------- */

type MomentSaisi = {
  ouvrageId: string | null;
  ligneId: string | null;
  heures: string;
  etat: EtatDuMoment;
  remarque: string;
};

type Brouillon = {
  /** Par `nature#ressourceId#position`. */
  moments: Map<string, MomentSaisi>;
  paniers: Map<string, boolean>;
  /**
   * La zone de transport choisie, par salarié : `""` = comme le chantier, `NON_APPLICABLE` =
   * aucune, sinon l'identifiant d'une zone du barème.
   *
   * **Les mêmes trois valeurs que la fiche du chantier** (`Chantiers.tsx`), délibérément : c'est
   * la même question posée un cran plus bas, et deux conventions pour « laisse le calcul faire »
   * finiraient par se contredire dans la tête de celui qui remplit les deux écrans.
   */
  zones: Map<string, string>;
  livraisons: Map<string, { quantiteRecue: string; remarque: string }>;
  /**
   * Les ressources ajoutées à l'écran et pas encore enregistrées (Chris, 18/09/2026).
   *
   * Dans le brouillon et non dans un état à part : `charger()` reconstruit les brouillons, donc
   * elles disparaissent d'elles-mêmes au rechargement — moment où le serveur les rend, cette
   * fois comme des ressources hors plan. Gardées à côté, elles auraient fait doublon avec
   * elles-mêmes après le premier enregistrement.
   */
  ajoutees: RessourceDeSaisie[];
  /** Les `nature#ressourceId` que l'envoi doit effacer. Vidé par le rechargement, comme le reste. */
  retirees: Set<string>;
};

/**
 * « Aucune zone pour cette personne », dans la liste déroulante.
 *
 * La même valeur sentinelle que la fiche du chantier, et pour la même raison : une chaîne vide
 * dit déjà « laisse le calcul décider », et il fallait un troisième état pour dire « non, rien » —
 * les confondre paierait l'indemnité pleine à quelqu'un qui n'y a pas droit (086).
 */
const SANS_ZONE = "NA";

/** Ce qu'un défaut de zone raconte, quand le chantier n'en retient aucune. */
const RIEN_A_DEDUIRE: Record<string, string> = {
  pas_de_site_de_reference: "aucun site de référence",
  distance_a_calculer: "distance à calculer",
  non_applicable: "NA — moins de 50 km",
  aucun_bareme: "aucun barème à cette date",
  hors_bareme: "hors barème",
};

function cleDuMoment(
  ressource: Pick<RessourceDeSaisie, "nature" | "ressourceId">,
  position: string,
): string {
  return `${ressource.nature}#${ressource.ressourceId}#${position}`;
}

/**
 * Le brouillon initial d'un chantier : ce qui est enregistré, sinon ce que le plan propose.
 *
 * **4 h et non les heures chiffrées** — décision de Chris du 16/09. Le chiffré s'affiche à côté ;
 * l'écart entre les deux est précisément ce que le suivi de chantier cherche, et le faire
 * disparaître en pré-remplissant avec le budget aurait vidé l'écran de son intérêt.
 */
export function brouillonInitial(
  chantier: ChantierDeSaisie,
  /**
   * Le barème accorde-t-il un panier sur ce chantier ?
   *
   * **Décoché quand la réponse est « NA »**, c'est-à-dire sous 50 km (règle de la 058). Le panier
   * est « proposé par défaut, décochable » (PRD §6.2) — mais pas là où le barème dit qu'il n'est
   * pas dû : proposer coché aurait payé un repas sur chaque journée d'un chantier à vingt
   * kilomètres, sans que personne n'ait à décocher quoi que ce soit pour que ça arrive. Mesuré :
   * le chantier d'essai est dans ce cas, et 218 des 4 052 communes le sont depuis le site de référence.
   *
   * Le responsable peut toujours cocher : c'est une proposition, pas un interdit.
   */
  panierDu: boolean,
): Brouillon {
  const moments = new Map<string, MomentSaisi>();
  for (const ressource of [...chantier.hommes, ...chantier.materiels]) {
    for (const moment of ressource.moments) {
      moments.set(cleDuMoment(ressource, moment.position), {
        ouvrageId: moment.saisi?.ouvrageId ?? moment.propose?.ouvrageId ?? null,
        ligneId: moment.propose?.ligneId ?? null,
        heures: moment.saisi?.heures ?? HEURES_PAR_DEMI_JOURNEE,
        etat: moment.saisi?.etat ?? "present",
        remarque: moment.saisi?.remarque ?? "",
      });
    }
  }
  return {
    moments,
    paniers: new Map(
      chantier.hommes.map((h) => [h.ressourceId, h.panier !== false && panierDu]),
    ),
    /*
      **La zone arrive sur ce que le chantier retient**, c'est-à-dire sur `""`.

      *« Par défaut tu positionnes la zone calculée »* (Chris, 18/09/2026) : c'est bien ce que
      l'écran affiche, mais en gardant le lien avec le calcul plutôt qu'en recopiant l'identifiant
      de la zone du jour. Recopier aurait figé la journée sur une distance que Chris est encore en
      train d'alimenter — et l'enregistrement, qui renvoie tout l'écran, aurait posé une
      correction sur chaque personne de chaque journée sans que personne ne l'ait demandé.
    */
    zones: new Map(
      chantier.hommes.map((h) => [
        h.ressourceId,
        h.zoneIndemnite?.sansObjet === true ? SANS_ZONE : (h.zoneIndemnite?.zoneId ?? ""),
      ]),
    ),
    ajoutees: [],
    retirees: new Set<string>(),
    livraisons: new Map(
      chantier.livraisons.map((l) => [
        l.livraisonId,
        {
          // **Le pointage des chauffeurs pré-remplit la case du chef** (Chris, 16/09/2026).
          // Ce qu'il a déjà confirmé l'emporte : sa correction ne doit pas être écrasée par le
          // total des chauffeurs au rechargement suivant.
          quantiteRecue: l.quantiteRecue ?? l.quantiteChauffeurs ?? "",
          remarque: l.remarque ?? "",
        },
      ]),
    ),
  };
}

/**
 * Le total d'heures d'une ressource sur la journée.
 *
 * Affiché parce que c'est le seul chiffre qu'on relit avant de valider — et parce qu'un total qui
 * dépasse la journée est le signe d'une correction ratée.
 */
export function totalDesHeures(
  brouillon: Brouillon,
  ressource: Pick<RessourceDeSaisie, "nature" | "ressourceId">,
): number {
  let total = 0;
  for (const position of ["matin", "apres_midi"] as const) {
    const saisi = brouillon.moments.get(cleDuMoment(ressource, position));
    if (saisi === undefined || saisi.etat !== "present") continue;
    const heures = Number(saisi.heures);
    if (Number.isFinite(heures)) total += heures;
  }
  return total;
}

/* ---------------------------------------------------------------------------
 * L'écran
 * ------------------------------------------------------------------------- */

export function SaisieHeures({
  cible,
}: {
  /**
   * La journée à ouvrir, quand on arrive depuis la pile du valideur (084).
   *
   * Nulle pour le chef : l'écran s'ouvre sur aujourd'hui et sur ses chantiers. Renseignée, elle
   * désigne un chantier où l'on n'est PAS affecté ce jour-là — le serveur ne l'ouvre que si le
   * circuit l'autorise, et il rend alors la journée entière plutôt que les seuls nœuds du compte.
   */
  readonly cible?: { chantierId: string; jour: string } | null;
} = {}): JSX.Element {
  const [jour, setJour] = useState<string>("");
  const [pour, setPour] = useState<string>("");
  const [journee, setJournee] = useState<JourneeDeSaisie | null>(null);
  const [brouillons, setBrouillons] = useState<Map<string, Brouillon>>(new Map());
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(
    async (options: { jour?: string; pour?: string; chantier?: string }) => {
    setChargement(true);
    setErreur(null);
    setMessage(null);
    try {
      const lu = await chargerJourneeDeSaisie(options);
      setJournee(lu);
      setJour(lu.jour);
      setBrouillons(
        new Map(
          lu.chantiers.map((c) => [
            c.chantierId,
            brouillonInitial(c, lu.zones[c.chantierId]?.panier.sansZone !== "non_applicable"),
          ]),
        ),
      );
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
      setJournee(null);
    } finally {
      setChargement(false);
    }
    },
    [],
  );

  /*
    **La cible du valideur commande le chargement**, et le jour qu'elle porte prime sur aujourd'hui.

    Dans les dépendances : sans elles, ouvrir une deuxième journée depuis la pile n'aurait rien
    rechargé — l'écran serait resté sur la première, en ayant l'air d'avoir répondu.
  */
  useEffect(() => {
    void charger(
      cible === null || cible === undefined
        ? {}
        : { jour: cible.jour, chantier: cible.chantierId },
    );
  }, [charger, cible?.chantierId, cible?.jour]);

  const modifier = (chantierId: string, applique: (brouillon: Brouillon) => void): void => {
    setBrouillons((precedent) => {
      const copie = new Map(precedent);
      const brouillon = copie.get(chantierId);
      if (brouillon === undefined) return precedent;
      const neuf: Brouillon = {
        moments: new Map(brouillon.moments),
        paniers: new Map(brouillon.paniers),
        zones: new Map(brouillon.zones),
        livraisons: new Map(brouillon.livraisons),
        ajoutees: [...brouillon.ajoutees],
        retirees: new Set(brouillon.retirees),
      };
      applique(neuf);
      copie.set(chantierId, neuf);
      return copie;
    });
  };

  const enregistrer = async (
    chantier: ChantierDeSaisie,
    clore: boolean,
    rouvrir = false,
  ): Promise<void> => {
    const brouillon = brouillons.get(chantier.chantierId);
    if (brouillon === undefined) return;
    setEnCours(true);
    setErreur(null);
    setMessage(null);
    try {
      /*
        Ce que l'envoi porte : le plan, les ajouts de l'écran, et rien de ce qui est retiré.

        Les retirées sortent **ici** et non à l'affichage seul : les laisser dans les moments les
        aurait réécrites juste après l'effacement, dans la même transaction.
      */
      const ressources = [
        ...chantier.hommes,
        ...chantier.materiels,
        ...brouillon.ajoutees,
      ].filter((r) => !brouillon.retirees.has(`${r.nature}#${r.ressourceId}`));
      await enregistrerLaSaisie({
        chantierId: chantier.chantierId,
        jour: chantier.jourRelatif > 0 ? (journee?.jour ?? jour) : jour,
        jourRelatif: chantier.jourRelatif,
        moments: ressources.flatMap((ressource) =>
          ressource.moments.flatMap((moment) => {
            const saisi = brouillon.moments.get(cleDuMoment(ressource, moment.position));
            if (saisi === undefined) return [];
            return [
              {
                nature: ressource.nature,
                ressourceId: ressource.ressourceId,
                libelle: ressource.libelle,
                detail: ressource.detail,
                position: moment.position,
                ouvrageId: saisi.ouvrageId,
                ligneId: saisi.ligneId,
                heures: saisi.heures,
                etat: saisi.etat,
                remarque: saisi.remarque === "" ? null : saisi.remarque,
              },
            ];
          }),
        ),
        // Le panier ne se déclare que pour les gens PRÉSENTS sur la saisie de l'affaire
        // (Chris, 16/09/2026) : une personne dont aucune demi-journée n'est déclarée présente
        // n'a pas de panier, et lui en poser un serait payer un repas que personne n'a pris.
        paniers: ressources
          .filter((r) => r.nature === "salarie" && totalDesHeures(brouillon, r) > 0)
          .map((h) => {
            const choix = brouillon.zones.get(h.ressourceId) ?? "";
            return {
              salarieId: h.ressourceId,
              panier: brouillon.paniers.get(h.ressourceId) !== false,
              /*
                Les trois états de la 086, traduits une seule fois. `""` n'envoie ni zone ni
                « sans objet » : c'est « comme le chantier », et le serveur efface alors toute
                correction — c'est ce qui rend la personne au calcul sans bouton dédié.
              */
              zoneIndemniteId: choix === "" || choix === SANS_ZONE ? null : choix,
              zoneIndemniteSansObjet: choix === SANS_ZONE,
            };
          }),
        retirees: [...brouillon.retirees].map((cle) => {
          const [nature, ressourceId] = cle.split("#");
          return {
            nature: nature === "materiel" ? ("materiel" as const) : ("salarie" as const),
            ressourceId: ressourceId ?? "",
          };
        }),
        livraisons: chantier.livraisons.map((l) => {
          const declaree = brouillon.livraisons.get(l.livraisonId);
          return {
            livraisonId: l.livraisonId,
            ouvrageId: l.ouvrageId,
            libelle: l.libelle,
            unite: l.unite,
            quantitePrevue: l.quantitePrevue,
            quantiteRecue:
              declaree === undefined || declaree.quantiteRecue.trim() === ""
                ? null
                : declaree.quantiteRecue.trim(),
            remarque:
              declaree === undefined || declaree.remarque.trim() === ""
                ? null
                : declaree.remarque.trim(),
          };
        }),
        clore,
        rouvrir,
      });
      // Le chantier ciblé est conservé : le valideur a ouvert une journée d'un chantier où il
      // n'est pas affecté, et l'oublier ici lui aurait vidé l'écran après son propre
      // enregistrement.
      await charger({ jour, pour, chantier: cible?.chantierId });
      /*
        **Le message se pose APRÈS le rechargement, jamais avant.**

        Il se posait avant, et `charger()` commence par `setMessage(null)` : la confirmation était
        donc effacée dans la milliseconde, et **aucun enregistrement n'a jamais rien affiché**
        depuis la 081. Chris l'a dit le 18/09 en cherchant ailleurs — *« j'ai validé la journée du
        23 mais je n'ai rien vu se passer »* —, et il a raison deux fois : le bouton promettait
        une validation qu'il ne faisait pas, et le seul retour qui l'aurait détrompé n'arrivait
        pas. Mesuré au navigateur avant correction : zéro message après un enregistrement réussi.
      */
      setMessage(
        clore
          ? `Journée de ${chantier.apo} clôturée : elle part en validation.`
          : rouvrir
            ? `Journée de ${chantier.apo} rouverte : le chantier peut la reprendre.`
            : `Journée de ${chantier.apo} enregistrée.`,
      );
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(false);
    }
  };

  /**
   * Enregistrer ce qui est à l'écran, puis franchir l'étape.
   *
   * Les deux dans cet ordre et jamais l'inverse : le valideur corrige une heure, puis valide. Une
   * validation qui partirait seule aurait fait monter la journée d'avant sa correction — donc
   * exactement ce qu'il venait de refuser, sans que rien ne le dise.
   */
  const enregistrerPuisValider = async (chantier: ChantierDeSaisie): Promise<void> => {
    await enregistrer(chantier, false);
    setEnCours(true);
    setErreur(null);
    try {
      await validerLaJournee({
        chantierId: chantier.chantierId,
        jour: journee?.jour ?? jour,
      });
      await charger({ jour, pour, chantier: cible?.chantierId });
      // Après le rechargement : `charger()` commence par effacer le message.
      setMessage(`Journée de ${chantier.apo} validée : elle poursuit son circuit.`);
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(false);
    }
  };

  const defaire = async (chantier: ChantierDeSaisie): Promise<void> => {
    setEnCours(true);
    setErreur(null);
    setMessage(null);
    try {
      await defaireLaValidation({
        chantierId: chantier.chantierId,
        jour: journee?.jour ?? jour,
      });
      await charger({ jour, pour, chantier: cible?.chantierId });
      // Après le rechargement, pour la même raison qu'au-dessus.
      setMessage(`Dernière validation de ${chantier.apo} défaite : la journée redescend d'un cran.`);
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(false);
    }
  };

  const allerAu = (decalage: number): void => {
    const cible = ajouterJours(jour === "" ? (journee?.jour ?? "") : jour, decalage);
    setJour(cible);
    void charger({ jour: cible, pour });
  };

  return (
    <section className="saisie-heures">
      <div className="saisie-heures__barre">
        <button type="button" onClick={() => { allerAu(-1); }} disabled={chargement}>
          ← veille
        </button>
        <label>
          Journée
          <input
            type="date"
            value={jour}
            onChange={(e) => {
              setJour(e.target.value);
              void charger({ jour: e.target.value, pour });
            }}
          />
        </label>
        <button type="button" onClick={() => { allerAu(1); }} disabled={chargement}>
          lendemain →
        </button>

        {/*
          **Le sélecteur de l'administrateur** (Chris, 16/09/2026) : « ils pourront choisir sur
          quelle personne se connecter […] ceci permettra parfois de remplacer une personne
          absente ». Ce n'est pas un confort : aujourd'hui, une seule des onze personnes
          affectées aux deux chantiers a un compte, les autres partagent une adresse de service.

          Ce qui est saisi reste attribué au compte réel : remplacer quelqu'un n'efface pas qui a
          saisi.
        */}
        {journee?.estAdmin === true && (
          <label className="saisie-heures__pour">
            Je saisis pour
            <select
              value={pour}
              onChange={(e) => {
                setPour(e.target.value);
                void charger({ jour, pour: e.target.value });
              }}
            >
              <option value="">moi-même</option>
              {journee.personnesPossibles.map((p) => (
                <option key={p.salarieId} value={p.salarieId}>
                  {p.libelle}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {erreur !== null && <p className="erreur">{erreur}</p>}
      {message !== null && <p className="alerte-douce">{message}</p>}
      {chargement && <p>Lecture du planning…</p>}

      {/*
        **Deux écrans vides, et ils ne se corrigent pas au même endroit** (18/09/2026).

        L'administrateur sans fiche n'est sur aucun chantier : ce n'est pas un défaut, c'est sa
        situation normale, et ce qu'il lui faut est le sélecteur ci-dessus. Lui servir « c'est le
        plan qu'il faut corriger » l'envoyait modifier un plan de charge qui n'a rien à se
        reprocher.
      */}
      {!chargement &&
        journee !== null &&
        journee.chantiers.length === 0 &&
        journee.pour.salarieId === null && (
          <p className="saisie-heures__vide">
            Votre compte n&apos;est rattaché à aucune fiche du référentiel : vous n&apos;avez pas de
            journée à vous. Choisissez une personne dans « Je saisis pour… » ci-dessus pour ouvrir
            la sienne.
          </p>
        )}

      {!chargement &&
        journee !== null &&
        journee.chantiers.length === 0 &&
        journee.pour.salarieId !== null && (
          <p className="saisie-heures__vide">
            Aucun chantier pour {journee.pour.nom ?? "cette personne"} le{" "}
            {formaterDate(journee.jour)}. La saisie part de l&apos;affectation du plan de charge :
            une personne ou une machine venue en renfort s&apos;ajoute dans la journée du chantier,
            mais un chantier absent d&apos;ici veut dire que personne n&apos;y est affecté ce
            jour-là — c&apos;est le plan qu&apos;il faut corriger.
          </p>
        )}

      {!chargement &&
        journee !== null &&
        journee.chantiers.map((chantier) => {
          const brouillon = brouillons.get(chantier.chantierId);
          if (brouillon === undefined) return null;
          const zones = journee.zones[chantier.chantierId] ?? null;
          /*
            **Le verrou vient du circuit, calculé par le serveur** (084).

            Et non d'un statut lu ici : le conducteur MODIFIE une journée close, l'assistante
            modifie une journée déjà validée par le conducteur. Deux règles — une à l'écran, une
            à la route — auraient fini par se contredire, et c'est l'écran qui aurait eu tort aux
            yeux de l'utilisateur.
          */
          /*
            **`!modifiable` suffit désormais**, et la condition sur le circuit est partie.

            Elle datait de la 084, où le seul motif de verrou était l'étape atteinte : avant
            clôture, `circuit` valait nul et `modifiable` valait vrai, donc les deux disaient la
            même chose. Depuis la 087, une journée **jamais saisie** peut appartenir à quelqu'un
            d'autre — le chef de chantier —, et il n'y a alors aucun circuit à interroger. Garder
            le test aurait laissé l'écran ouvert exactement dans le cas qu'on vient de fermer.
          */
          const verrouille = !chantier.modifiable;

          return (
            <article key={chantier.chantierId} className="saisie-heures__chantier">
              <header className="saisie-heures__entete">
                <h2>
                  {chantier.apo} — {chantier.nom}
                </h2>
                <p className="saisie-heures__noeuds">
                  {chantier.noeuds.map(nommerLeNoeud).join(" · ")}
                </p>
                <p className="saisie-heures__zones">
                  {/*
                    Les zones se déduisent à chaque affichage (règle de la 058) : ce qui est
                    montré ici est ce que le chantier porte aujourd'hui, jamais une valeur
                    recopiée le jour de sa création.

                    **Ce que cette ligne dit a changé le 18/09/2026** : elle annonçait « la » zone
                    de transport, alors que la zone se retient maintenant personne par personne
                    dans la colonne du tableau. Elle nomme donc ce qu'elle est — le barème du
                    chantier, c'est-à-dire ce que chaque ligne prend par défaut. Deux endroits qui
                    répondraient tous deux « la zone de cette journée » auraient fini par se
                    contredire, et c'est le défaut le plus répété de ce produit.
                  */}
                  Barème du chantier — transport :{" "}
                  <strong>
                    {zones?.indemnite.zone?.libelle ??
                      RIEN_A_DEDUIRE[zones?.indemnite.sansZone ?? ""] ??
                      "non déterminée"}
                  </strong>{" "}
                  · panier :{" "}
                  <strong>
                    {zones?.panier.sansZone === "non_applicable"
                      ? "NA (moins de 50 km)"
                      : (zones?.panier.zone?.libelle ?? "non déterminé")}
                  </strong>
                </p>
                {/*
                  **Qui saisit cette journée** (087, Chris 18/09/2026).

                  Affiché sur toutes les journées et pas seulement sur celles qu'on ne peut pas
                  écrire : celui qui saisit doit savoir qu'il saisit **pour tout le monde**, sinon
                  il déclare trois personnes et clôture en croyant avoir fini. C'est la phrase qui
                  explique pourquoi l'écran montre des gens qu'il n'a pas encadrés lui-même.

                  Un écran verrouillé sans raison écrite se lit comme une panne — la leçon des
                  étapes du circuit, qui disent toujours qui attend.
                */}
                {chantier.saisiePar !== null && (
                  <p className="saisie-heures__responsable">
                    Saisie de la journée : <strong>{chantier.saisiePar.libelle}</strong>{" "}
                    <span className="saisie-heures__detail-en-ligne">
                      ({chantier.saisiePar.libelleCode}) — il déclare tout le chantier
                    </span>
                    {!chantier.saisiePar.peutSeConnecter && (
                      <span className="en-ecart">
                        {" "}
                        · aucun compte pour lui : personne ne peut saisir cette journée tant qu&apos;un
                        compte ne lui est pas ouvert, ou qu&apos;un administrateur ne saisit pas à sa
                        place.
                      </span>
                    )}
                  </p>
                )}
                {verrouille && chantier.saisiePar !== null && chantier.statut === "aucune" && (
                  <p className="saisie-heures__note en-ecart">
                    Vous ne remplissez pas cette feuille : vos heures y sont déclarées par{" "}
                    {chantier.saisiePar.libelle}. Signalez-lui une correction plutôt que de tenir
                    une seconde feuille.
                  </p>
                )}
                <p className={`saisie-heures__statut saisie-heures__statut--${chantier.statut}`}>
                  {chantier.statut === "aucune" && "Journée non saisie"}
                  {chantier.statut === "ouverte" && "Journée en cours de saisie"}
                  {chantier.statut !== "aucune" &&
                    chantier.statut !== "ouverte" &&
                    direOuEnEstLaJournee(chantier)}
                </p>
                {chantier.circuit !== null && <FriseDuCircuit circuit={chantier.circuit} />}
              </header>

              {/*
                Les ressources ajoutées à l'écran rejoignent le bloc de leur nature, et non un
                troisième bloc « ajouts » : ce qui intéresse celui qui relit sa journée est qui a
                travaillé, pas par quel chemin la ligne est arrivée. C'est la mention « hors plan »
                qui porte la distinction, là où elle se lit — sur la ligne.
              */}
              <BlocRessources
                titre="Personnel"
                ressources={[
                  ...chantier.hommes,
                  ...brouillon.ajoutees.filter((r) => r.nature === "salarie"),
                ].filter((r) => !brouillon.retirees.has(`${r.nature}#${r.ressourceId}`))}
                brouillon={brouillon}
                noeuds={chantier.noeuds}
                verrouille={verrouille}
                avecPanier
                zoneDuChantier={zones?.indemnite ?? null}
                baremeIndemnites={journee.baremeIndemnites}
                ajoutables={journee.ajoutables.salaries}
                referentielLu={journee.ajoutables.referentielLu}
                onChange={(applique) => { modifier(chantier.chantierId, applique); }}
              />

              <BlocRessources
                titre="Matériel"
                ressources={[
                  ...chantier.materiels,
                  ...brouillon.ajoutees.filter((r) => r.nature === "materiel"),
                ].filter((r) => !brouillon.retirees.has(`${r.nature}#${r.ressourceId}`))}
                brouillon={brouillon}
                noeuds={chantier.noeuds}
                verrouille={verrouille}
                avecPanier={false}
                zoneDuChantier={null}
                baremeIndemnites={journee.baremeIndemnites}
                ajoutables={journee.ajoutables.materiels}
                referentielLu={journee.ajoutables.referentielLu}
                onChange={(applique) => { modifier(chantier.chantierId, applique); }}
              />

              {chantier.livraisons.length > 0 && (
                <section className="saisie-heures__bloc">
                  <h3>Livraisons reçues</h3>
                  {/* Toutes les provenances : « oui le chef valide les livraisons du
                      fournisseur » (Chris, 16/09/2026). Ce qu'il confirme n'est pas le même fait
                      selon la provenance — pour nos camions il recoupe ce que le chauffeur a
                      pointé, pour un fournisseur il constate seul — d'où la colonne qui la nomme.

                      **Et tous les nœuds du chantier depuis le 17/09/2026**, pas seulement ceux
                      où le responsable travaille : deux pastilles de la production ne tombaient
                      sur le nœud de personne, donc personne ne pouvait les confirmer. */}
                  {/*
                    **Le récap, en une ligne** — *demande de Chris, 17/09/2026 : « que le
                    responsable ait aussi dans sa journée le récap de ce qui a été livré ».*

                    Un COMPTE, et non une somme de tonnages : les livraisons d'un même jour se
                    comptent en tonnes, en mètres cubes et à l'unité (règle de la 081, tenue au
                    pied du plan de charge). « 62 » sous trois unités mélangées serait un nombre
                    que personne ne pourrait rapprocher de rien.

                    Ce qu'il dit est ce qui décide de clôturer ou non : combien reste-t-il de
                    cases vides. Sans ce compte, il faut parcourir le tableau des yeux pour
                    savoir si on a tout fait — et sur un chantier à huit pastilles, on croit
                    avoir fini.
                  */}
                  {(() => {
                    const confirmees = chantier.livraisons.filter(
                      (l) => (brouillon.livraisons.get(l.livraisonId)?.quantiteRecue ?? "") !== "",
                    ).length;
                    const restant = chantier.livraisons.length - confirmees;
                    return (
                      <p className="saisie-heures__recap">
                        {chantier.livraisons.length === 1
                          ? "1 livraison attendue sur le chantier ce jour"
                          : `${String(chantier.livraisons.length)} livraisons attendues sur le chantier ce jour`}{" "}
                        · <strong>{confirmees}</strong> avec une quantité reçue
                        {restant > 0 && (
                          <span className="en-ecart">
                            {" "}
                            · {restant} encore {restant === 1 ? "vide" : "vides"}
                          </span>
                        )}
                      </p>
                    );
                  })()}
                  <div className="tableau-defilant">
                    <table className="saisie-heures__table">
                      <thead>
                        <tr>
                          <th>Nœud</th>
                          <th>Matériau</th>
                          <th>Provenance</th>
                          <th>Prévu</th>
                          <th>Chauffeurs</th>
                          <th>Reçu</th>
                          <th>Remarque</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chantier.livraisons.map((livraison) => {
                          const declaree = brouillon.livraisons.get(livraison.livraisonId);
                          return (
                            <tr key={livraison.livraisonId}>
                              <td data-colonne="Nœud">{livraison.numero}</td>
                              <td data-colonne="Matériau">{livraison.libelle}</td>
                              <td data-colonne="Provenance">{livraison.provenance}</td>
                              <td data-colonne="Prévu">
                                {formaterNombre(livraison.quantitePrevue, 2)} {livraison.unite}
                              </td>
                              {/* Ce que les chauffeurs ont déclaré, NOMMÉ : « confirmer » suppose
                                  de savoir qui a dit quoi, et un total anonyme ne se conteste
                                  pas. Un tiret quand personne n'a pointé — il n'y a alors rien à
                                  confirmer, ce qui est le cas de tout ce qu'un fournisseur livre. */}
                              <td data-colonne="Chauffeurs">
                                {livraison.quantiteChauffeurs === null ? (
                                  <span className="saisie-heures__sans">—</span>
                                ) : (
                                  <>
                                    <span className="saisie-heures__nom">
                                      {formaterNombre(livraison.quantiteChauffeurs, 2)}{" "}
                                      {livraison.unite}
                                    </span>
                                    {livraison.pointages.map((p, rang) => (
                                      <span
                                        key={`${p.libelle}#${String(rang)}`}
                                        className="saisie-heures__detail"
                                      >
                                        {p.libelle}
                                        {p.voyages === null
                                          ? ""
                                          : ` · ${String(p.voyages)} voyage(s)`}
                                        {/* Le code ERP de ce que la carrière a CHARGÉ, lu sur le
                                            bon — et non celui que le planning annonçait. */}
                                        {p.bons.length > 0 && (
                                          <>
                                            {" · "}
                                            {p.bons.length} bon(s)
                                            {p.bons[0]?.codeErp !== null &&
                                              p.bons[0]?.codeErp !== undefined && (
                                                <> · {p.bons[0].codeErp}</>
                                              )}
                                            {" "}
                                            {p.bons.map((b) => (
                                              <a
                                                key={b.id}
                                                href={adresseDuBon(b.id)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="saisie-heures__bon"
                                              >
                                                voir
                                              </a>
                                            ))}
                                          </>
                                        )}
                                      </span>
                                    ))}
                                  </>
                                )}
                              </td>
                              <td data-colonne="Reçu">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  inputMode="decimal"
                                  className="saisie-heures__quantite"
                                  placeholder="non pointé"
                                  disabled={verrouille}
                                  value={declaree?.quantiteRecue ?? ""}
                                  onChange={(e) => {
                                    modifier(chantier.chantierId, (b) => {
                                      b.livraisons.set(livraison.livraisonId, {
                                        quantiteRecue: e.target.value,
                                        remarque: declaree?.remarque ?? "",
                                      });
                                    });
                                  }}
                                />{" "}
                                {livraison.unite}
                              </td>
                              <td data-colonne="Remarque">
                                <input
                                  type="text"
                                  disabled={verrouille}
                                  value={declaree?.remarque ?? ""}
                                  onChange={(e) => {
                                    modifier(chantier.chantierId, (b) => {
                                      b.livraisons.set(livraison.livraisonId, {
                                        quantiteRecue: declaree?.quantiteRecue ?? "",
                                        remarque: e.target.value,
                                      });
                                    });
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="saisie-heures__note">
                    Une case vide veut dire « pas encore pointé », et non « rien reçu » : mettre 0
                    déclare qu'aucune quantité n'est arrivée.
                  </p>
                </section>
              )}

              <div className="saisie-heures__actions">
                {/*
                  **« Enregistrer » et non « Tout valider »** (18/09/2026).

                  Ce bouton écrit la journée et ne la fait pas monter d'un cran : le mot
                  « valider » promettait le geste du bouton suivant. Chris a clôturé sa recherche
                  là-dessus — *« j'ai validé la journée du 23 mais je n'ai rien vu se passer »* —,
                  et il avait cliqué exactement ce qu'il fallait pour enregistrer, rien pour
                  transmettre. Deux boutons voisins dont l'un dit le nom de l'autre ne se
                  départagent par aucune lecture attentive.
                */}
                <button
                  type="button"
                  disabled={enCours || verrouille}
                  title="Écrit la journée sans la transmettre. Pour la faire partir en validation, utiliser « Clôturer la journée »."
                  onClick={() => { void enregistrer(chantier, false); }}
                >
                  Enregistrer
                </button>
                <button
                  type="button"
                  className="saisie-heures__clore"
                  disabled={enCours || verrouille || chantier.statut !== "ouverte"}
                  onClick={() => { void enregistrer(chantier, true); }}
                >
                  Clôturer la journée
                </button>
                {/*
                  **Franchir l'étape depuis la journée elle-même** (084).

                  Le bouton n'existe que si le serveur a dit que ce compte peut le faire : c'est
                  la même réponse qui refusera l'appel, donc il ne peut pas être visible et
                  répondre 409 — ce qui passerait pour une panne.

                  Enregistrer d'abord, valider ensuite : le valideur qui corrige une heure puis
                  clique « Valider » perdrait sa correction si la validation partait seule, et
                  aurait validé exactement ce qu'il venait de refuser.
                */}
                {chantier.aValider !== null && (
                  <button
                    type="button"
                    className="primaire"
                    disabled={enCours}
                    onClick={() => { void enregistrerPuisValider(chantier); }}
                  >
                    Enregistrer et valider
                  </button>
                )}
                {/*
                  **Rouvrir est un bouton, et non l'effet de bord d'un enregistrement** (084).

                  Il n'apparaît que sur une journée close que personne n'a encore validée : dès
                  qu'une étape est franchie, on la défait — c'est tracé, rouvrir ne l'est pas.
                */}
                {chantier.statut === "close" &&
                  !verrouille &&
                  (chantier.circuit === null ||
                    !chantier.circuit.etapes.some((e) => e.franchie !== null)) && (
                    <button
                      type="button"
                      disabled={enCours}
                      title="Ramener la journée au chantier pour qu'il la corrige lui-même."
                      onClick={() => { void enregistrer(chantier, false, true); }}
                    >
                      Rouvrir la journée
                    </button>
                  )}
                {/*
                  Défaire sa propre validation, comme pour les contre-études (049, 065) : sans ce
                  geste, une erreur imposerait une journée corrective, qui ment sur la date.
                */}
                {chantier.circuit !== null &&
                  chantier.circuit.etapes.some((e) => e.franchie !== null) && (
                    <button
                      type="button"
                      className="saisie-heures__defaire"
                      disabled={enCours}
                      onClick={() => { void defaire(chantier); }}
                    >
                      Défaire la dernière validation
                    </button>
                  )}
              </div>
            </article>
          );
        })}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Le circuit de validation (084)
 * ------------------------------------------------------------------------- */

/**
 * Où en est la journée, en une phrase.
 *
 * Elle nomme **qui attend**, et non l'état atteint : « close » est le vocabulaire du chef, et le
 * conducteur qui lit son écran veut savoir que c'est son tour. Quand plus rien n'attend, elle dit
 * que la journée est partie — c'est ce qui explique pourquoi elle ne se modifie plus.
 */
export function direOuEnEstLaJournee(chantier: ChantierDeSaisie): string {
  const circuit = chantier.circuit;
  if (circuit === null) return "Journée clôturée";
  if (circuit.terminee) {
    return "Journée validée de bout en bout — elle est partie en paye et ne se modifie plus";
  }
  const attendue = circuit.attendue;
  if (attendue === null) return "Journée clôturée — aucune validation n'est prévue";
  if (attendue.empechement !== null) return `Journée bloquée : ${attendue.empechement}`;
  return (
    `Journée clôturée — en attente de « ${attendue.libelle} »` +
    (attendue.nomme === null ? "" : ` (${attendue.nomme})`)
  );
}

/**
 * Les étapes du circuit, franchies et à venir.
 *
 * **Les étapes non prévues ne s'affichent pas du tout.** Les montrer grisées aurait fait croire
 * qu'un contrôle est en panne, là où l'installation ne l'a simplement pas : le conducteur d'un
 * client qui n'a pas d'assistante référente n'a pas à se demander pourquoi une ligne reste vide.
 */
function FriseDuCircuit({ circuit }: { readonly circuit: CircuitDeLaJournee }): JSX.Element | null {
  const visibles = circuit.etapes.filter((e) => e.prevue);
  if (visibles.length === 0) return null;
  return (
    <ol className="saisie-heures__circuit">
      {visibles.map((etape) => (
        <li
          key={etape.etape}
          className={
            etape.franchie !== null
              ? "franchie"
              : circuit.attendue?.etape === etape.etape
                ? "attendue"
                : undefined
          }
        >
          <span className="saisie-heures__circuit-role">{etape.libelle}</span>
          <span className="saisie-heures__circuit-etat">
            {etape.franchie !== null
              ? `validé ${formaterDate(etape.franchie.le.slice(0, 10))}${
                  etape.franchie.par === null ? "" : ` par ${etape.franchie.par}`
                }`
              : etape.empechement !== null
                ? "bloqué"
                : (etape.nomme ?? "en attente")}
          </span>
          {etape.franchie?.commentaire !== null && etape.franchie !== null && (
            <span className="saisie-heures__circuit-mot">« {etape.franchie.commentaire} »</span>
          )}
        </li>
      ))}
    </ol>
  );
}

/* ---------------------------------------------------------------------------
 * Un bloc de ressources — les hommes, puis les machines
 * ------------------------------------------------------------------------- */

function BlocRessources({
  titre,
  ressources,
  brouillon,
  noeuds,
  verrouille,
  avecPanier,
  zoneDuChantier,
  baremeIndemnites,
  ajoutables,
  referentielLu,
  onChange,
}: {
  titre: string;
  ressources: readonly RessourceDeSaisie[];
  brouillon: Brouillon;
  noeuds: readonly { ouvrageId: string; numero: string; libelle: string }[];
  verrouille: boolean;
  avecPanier: boolean;
  /** Ce que le chantier retient, c'est-à-dire ce que « comme le chantier » vaut. Nul sur les machines. */
  zoneDuChantier: ZoneRetenue | null;
  baremeIndemnites: readonly BaremePeriode[];
  /** Ce que le référentiel propose d'ajouter — les salariés ici, les machines là. */
  ajoutables: readonly RessourceAjoutable[];
  referentielLu: boolean;
  onChange: (applique: (brouillon: Brouillon) => void) => void;
}): JSX.Element | null {
  /*
    **Le bloc reste si l'on peut encore ajouter**, même vide.

    Il disparaissait dès qu'il n'y avait rien à montrer, ce qui était juste tant que son contenu
    venait du plan seul. Depuis le 18/09, c'est là que vit le bouton d'ajout : un chantier sans
    matériel planifié n'aurait plus eu aucun endroit où déclarer la machine qui est venue.
  */
  if (ressources.length === 0 && verrouille) return null;

  /*
    Les tranches de LA PÉRIODE que le chantier retient, jamais tout le barème. Un barème est daté,
    et proposer les zones de 2026 sur un chantier de 2027 ferait choisir un montant qui n'a plus
    cours. C'est la règle de la fiche du chantier, reprise mot pour mot.
  */
  const zonesProposees = avecPanier
    ? (baremeIndemnites.find((p) => p.periodeId === zoneDuChantier?.periodeId)?.zones ?? [])
    : [];

  return (
    <section className="saisie-heures__bloc">
      <h3>{titre}</h3>
      {/*
        **Pas d'en-têtes de colonnes au-dessus de rien.** Vu au navigateur le 18/09 : le bloc
        Matériel d'une journée sans machine planifiée affichait « Qui · Demi-journée · Nœud · … »
        suivi du vide, ce qui se lit comme un tableau qui n'a pas chargé.
      */}
      {ressources.length > 0 && (
      <div className="tableau-defilant">
        <table className="saisie-heures__table">
          <thead>
            <tr>
              <th>Qui</th>
              <th>Demi-journée</th>
              <th>Nœud</th>
              <th>Heures</th>
              <th>État</th>
              {avecPanier && <th>Zone transport</th>}
              {avecPanier && <th>Panier</th>}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {ressources.map((ressource) => {
              const total = totalDesHeures(brouillon, ressource);
              return ressource.moments.map((moment, rang) => {
                const cle = cleDuMoment(ressource, moment.position);
                const saisi = brouillon.moments.get(cle);
                if (saisi === undefined) return null;
                return (
                  <tr key={cle} className="saisie-heures__ligne">
                    <td data-colonne="Qui">
                      {rang === 0 && (
                        <>
                          <span className="saisie-heures__nom">{ressource.libelle}</span>
                          {ressource.detail !== null && (
                            <span className="saisie-heures__detail">{ressource.detail}</span>
                          )}
                          {/*
                            **Une ressource hors plan se NOMME, et c'est la seule qu'on retire.**

                            Le plan ne la place pas : sans cette mention, celui qui relit la
                            journée la prendrait pour une affectation et chercherait au plan de
                            charge pourquoi elle y est — alors qu'elle a été ajoutée à la main.

                            Le retrait n'existe que là. Une ligne que le plan place reviendrait
                            d'elle-même au rechargement suivant, proposée à 4 h : un bouton qui
                            « efface » puis voit revenir sa ligne passe pour une panne. Ce qu'on
                            fait d'une personne qui n'est pas venue, c'est déclarer son état.
                          */}
                          {ressource.horsPlan && (
                            <>
                              <span className="saisie-heures__ajoutee">ajoutée — hors plan</span>
                              {!verrouille && (
                                <button
                                  type="button"
                                  className="saisie-heures__retirer"
                                  title="Retirer cette ressource de la journée. Effectif à l'enregistrement."
                                  onClick={() => {
                                    onChange((b) => {
                                      const cle = `${ressource.nature}#${ressource.ressourceId}`;
                                      b.retirees.add(cle);
                                      // Ses demi-journées partent aussi du brouillon : laissées
                                      // là, un ajout suivi d'un retrait puis d'un nouvel ajout
                                      // de la même personne aurait ressorti les heures du
                                      // premier, que personne n'avait redemandées.
                                      for (const position of ["matin", "apres_midi"]) {
                                        b.moments.delete(`${cle}#${position}`);
                                      }
                                      b.ajoutees = b.ajoutees.filter(
                                        (r) => `${r.nature}#${r.ressourceId}` !== cle,
                                      );
                                    });
                                  }}
                                >
                                  retirer
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </td>
                    <td data-colonne="Demi-journée">{NOM_POSITION[moment.position]}</td>
                    <td data-colonne="Nœud">
                      <select
                        disabled={verrouille}
                        value={saisi.ouvrageId ?? ""}
                        onChange={(e) => {
                          onChange((b) => {
                            b.moments.set(cle, {
                              ...saisi,
                              ouvrageId: e.target.value === "" ? null : e.target.value,
                            });
                          });
                        }}
                      >
                        <option value="">—</option>
                        {noeuds.map((n) => (
                          <option key={n.ouvrageId} value={n.ouvrageId}>
                            {n.numero}
                          </option>
                        ))}
                      </select>
                      {/* Le nœud évincé est NOMMÉ, jamais tu : un homme n'est qu'à un endroit à
                          9 heures, mais le chiffrage en prévoyait deux et il faut le savoir. */}
                      {moment.propose !== null && moment.propose.aussiPrevuSur.length > 0 && (
                        <span className="saisie-heures__aussi">
                          aussi prévu sur {moment.propose.aussiPrevuSur.join(", ")}
                        </span>
                      )}
                    </td>
                    <td data-colonne="Heures">
                      <input
                        type="number"
                        min="0"
                        max="24"
                        step="0.25"
                        inputMode="decimal"
                        className="saisie-heures__heures"
                        disabled={verrouille || saisi.etat !== "present"}
                        value={saisi.etat === "present" ? saisi.heures : "0"}
                        onChange={(e) => {
                          onChange((b) => {
                            b.moments.set(cle, { ...saisi, heures: e.target.value });
                          });
                        }}
                      />
                      {/* Le chiffré à côté du fait : c'est l'écart qui intéresse le suivi. */}
                      {moment.propose !== null && (
                        <span className="saisie-heures__chiffre">
                          chiffré {formaterNombre(moment.propose.heuresChiffrees, 2)} h
                        </span>
                      )}
                    </td>
                    <td data-colonne="État">
                      <select
                        disabled={verrouille}
                        value={saisi.etat}
                        onChange={(e) => {
                          onChange((b) => {
                            b.moments.set(cle, { ...saisi, etat: e.target.value as EtatDuMoment });
                          });
                        }}
                      >
                        {(Object.keys(NOMS_ETAT) as EtatDuMoment[]).map((etat) => (
                          <option key={etat} value={etat}>
                            {NOMS_ETAT[etat]}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/*
                      **La zone de transport, en colonne et en face du nom** — *demande de Chris
                      du 18/09/2026 : « il faudrait la présenter en colonne comme les paniers […]
                      par défaut tu positionnes la zone calculée et on doit pouvoir changer la
                      zone ou ne pas en mettre ».*

                      Elle ne vivait qu'en en-tête, en une phrase valable pour tout le monde. Sur
                      un chantier où quelqu'un vient par ses propres moyens, il n'y avait aucun
                      endroit pour le dire.

                      Sur la première ligne seulement, comme le panier et le total : la zone
                      appartient à la journée d'une personne, pas à sa matinée. La désactiver à
                      zéro heure suit la même règle que le panier — *« pour les panier et zone
                      c'est pour les gens présents »* (Chris, 16/09/2026).
                    */}
                    {avecPanier && (
                      <td data-colonne="Zone transport">
                        {rang === 0 && (
                          <ZoneDeLaLigne
                            choix={brouillon.zones.get(ressource.ressourceId) ?? ""}
                            duChantier={zoneDuChantier}
                            proposees={zonesProposees}
                            desactive={verrouille || total === 0}
                            onChoisir={(valeur) => {
                              onChange((b) => {
                                b.zones.set(ressource.ressourceId, valeur);
                              });
                            }}
                          />
                        )}
                      </td>
                    )}
                    {avecPanier && (
                      <td data-colonne="Panier">
                        {rang === 0 && (
                          <input
                            type="checkbox"
                            disabled={verrouille || total === 0}
                            checked={brouillon.paniers.get(ressource.ressourceId) !== false}
                            onChange={(e) => {
                              onChange((b) => {
                                b.paniers.set(ressource.ressourceId, e.target.checked);
                              });
                            }}
                          />
                        )}
                      </td>
                    )}
                    <td data-colonne="Total">
                      {rang === 0 && (
                        <span className={total > 10 ? "en-ecart" : undefined}>
                          {formaterNombre(total.toString(), 2)} h
                        </span>
                      )}
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
      )}
      {ressources.length === 0 && (
        <p className="saisie-heures__note">
          Rien de planifié ce jour-là. Ce qui est venu quand même s&apos;ajoute ci-dessous.
        </p>
      )}
      {/*
        Le barème n'a aucune tranche pour la période du chantier : le dire **une fois**, et non
        par personne. Répété sur chaque ligne (cinq fois sur la journée d'essai), il se lisait
        comme une alerte propre à chacun alors qu'il parle du paramétrage, qui est ailleurs.
      */}
      {avecPanier && ressources.length > 0 && zonesProposees.length === 0 && (
        <p className="saisie-heures__note en-ecart">
          Aucune zone de transport au barème à cette date : les zones ne se choisissent pas tant
          que le Paramétrage transport n&apos;en porte pas pour la période du chantier.
        </p>
      )}
      {!verrouille && (
        <Ajout
          nature={avecPanier ? "salarie" : "materiel"}
          ajoutables={ajoutables}
          referentielLu={referentielLu}
          dejaLa={new Set(ressources.map((r) => r.ressourceId))}
          noeuds={noeuds}
          onChange={onChange}
        />
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * La zone de transport d'une personne (086, Chris 18/09/2026)
 * ------------------------------------------------------------------------- */

/**
 * Les trois réponses possibles, dans une liste déroulante.
 *
 * **Le premier choix DIT ce que le calcul donne** — « comme le chantier : Zone2 (3,71 €) » — et
 * non « — automatique — » : sans cela, il faudrait choisir pour savoir ce qu'on abandonne. C'est
 * le parti pris de la fiche du chantier, et il vaut d'autant plus ici que celui qui remplit n'est
 * pas celui qui a paramétré le barème.
 *
 * **« Aucune zone » est un choix explicite et non un vide.** C'est la seule des trois réponses qui
 * enlève de l'argent à quelqu'un : la confondre avec « comme le chantier » aurait payé l'indemnité
 * pleine à qui n'y a pas droit, et une journée qui a l'air juste ne se corrige jamais.
 */
function ZoneDeLaLigne({
  choix,
  duChantier,
  proposees,
  desactive,
  onChoisir,
}: {
  choix: string;
  duChantier: ZoneRetenue | null;
  proposees: readonly ZoneProposee[];
  desactive: boolean;
  onChoisir: (valeur: string) => void;
}): JSX.Element {
  /*
    **Court, parce qu'une liste déroulante TRONQUE.**

    Mesuré au navigateur à 390 px et à 1440 px : « — comme le chantier : aucun site de référence — »
    s'affichait « — comme le chantier : aucun site de », donc le premier choix ne disait plus rien
    du tout — exactement le « — automatique — » muet qu'on voulait éviter. Le motif détaillé vit
    dans l'en-tête du chantier, qui a la largeur pour lui et ne le répète pas par personne.
  */
  const calcul =
    duChantier === null
      ? null
      : duChantier.zone !== null
        ? `${duChantier.zone.libelle} (${valeurDeLaZone(duChantier.zone)})`
        : "aucune";

  return (
    <>
      <select
        className="saisie--deduite"
        disabled={desactive}
        value={choix}
        onChange={(e) => {
          onChoisir(e.target.value);
        }}
      >
        <option value="">
          {calcul === null ? "comme le chantier" : `comme le chantier : ${calcul}`}
        </option>
        <option value={SANS_ZONE}>Aucune zone</option>
        {proposees.map((zone) => (
          <option key={zone.id} value={zone.id}>
            {zone.libelle} — {valeurDeLaZone(zone)}
          </option>
        ))}
      </select>
      {/*
        Ce qui est choisi à la main se signale, comme sur la fiche du chantier : c'est la seule
        chose qui distingue une journée où quelqu'un a tranché d'une journée que le calcul a
        remplie toute seule — et c'est cette différence qu'on relit six mois plus tard.
      */}
      {choix !== "" && <span className="saisie-heures__detail">choisi à la main</span>}
    </>
  );
}

/* ---------------------------------------------------------------------------
 * Ajouter une ressource que le plan n'a pas prévue (Chris, 18/09/2026)
 * ------------------------------------------------------------------------- */

/**
 * *« Il faut que le responsable puisse rajouter des ressources dans sa feuille de validation, donc
 * qu'il puisse rajouter une ou plusieurs ressources MO et matériel si besoin, en renseignant les
 * colonnes heures etc., état etc. »*
 *
 * ## Les demi-journées se choisissent à l'ajout
 *
 * Et non après, par un geste de suppression qui n'existe nulle part ailleurs dans cet écran. Le
 * grain du produit est la demi-journée (081) : quelqu'un venu prêter la main une matinée ne doit
 * pas arriver avec un après-midi à 4 h qu'il faudrait penser à remettre à zéro. Une demi-journée
 * oubliée à 4 h part en paye, c'est le sens dans lequel l'erreur coûte.
 *
 * ## Le nœud n'est pré-rempli que s'il n'y a pas de choix
 *
 * Le reste de l'écran propose parce que le plan propose ; ici il n'y a rien à proposer, et
 * désigner le premier nœud de la liste serait inventer une réponse. Quand le responsable n'a qu'un
 * nœud, en revanche, le choisir pour lui n'invente rien.
 */
function Ajout({
  nature,
  ajoutables,
  referentielLu,
  dejaLa,
  noeuds,
  onChange,
}: {
  nature: "salarie" | "materiel";
  ajoutables: readonly RessourceAjoutable[];
  referentielLu: boolean;
  /** Ce qui est déjà à l'écran : on ne propose pas d'ajouter quelqu'un qui y est. */
  dejaLa: ReadonlySet<string>;
  noeuds: readonly { ouvrageId: string; numero: string; libelle: string }[];
  onChange: (applique: (brouillon: Brouillon) => void) => void;
}): JSX.Element {
  const [quoi, setQuoi] = useState<string>("");
  const [quand, setQuand] = useState<"journee" | "matin" | "apres_midi">("journee");

  const libres = ajoutables.filter((r) => !dejaLa.has(r.ressourceId));

  const ajouter = (): void => {
    const choisie = libres.find((r) => r.ressourceId === quoi);
    if (choisie === undefined) return;
    const positions: readonly ("matin" | "apres_midi")[] =
      quand === "journee" ? ["matin", "apres_midi"] : [quand];

    onChange((b) => {
      const cle = `${nature}#${choisie.ressourceId}`;
      // Retirée puis rajoutée dans la même séance : l'ajout l'emporte, sinon l'envoi
      // l'effacerait aussitôt et le geste n'aurait aucun effet visible.
      b.retirees.delete(cle);
      for (const position of positions) {
        b.moments.set(`${cle}#${position}`, {
          ouvrageId: noeuds.length === 1 ? (noeuds[0]?.ouvrageId ?? null) : null,
          ligneId: null,
          heures: HEURES_PAR_DEMI_JOURNEE,
          etat: "present",
          remarque: "",
        });
      }
      /*
        Elle porte `horsPlan` dès l'écran, et non seulement après l'enregistrement : c'est ce qui
        rend son retrait possible tout de suite. Un ajout par erreur qu'il faudrait d'abord
        enregistrer pour pouvoir l'annuler serait un aller-retour dans la paye.
      */
      b.ajoutees = [
        ...b.ajoutees.filter((r) => `${r.nature}#${r.ressourceId}` !== cle),
        {
          nature,
          ressourceId: choisie.ressourceId,
          libelle: choisie.libelle,
          detail: choisie.detail,
          moments: positions.map((position) => ({ position, propose: null, saisi: null })),
          panier: nature === "salarie" ? true : null,
          zoneIndemnite: nature === "salarie" ? { zoneId: null, sansObjet: false } : null,
          horsPlan: true,
        },
      ];
    });
    setQuoi("");
  };

  if (!referentielLu) {
    return (
      <p className="saisie-heures__note en-ecart">
        Le référentiel ne répond pas : impossible d'ajouter une ressource pour le moment. Les heures
        déjà à l'écran s'enregistrent normalement.
      </p>
    );
  }

  return (
    <div className="saisie-heures__ajout">
      <label>
        Ajouter
        <select
          value={quoi}
          onChange={(e) => {
            setQuoi(e.target.value);
          }}
        >
          <option value="">{nature === "salarie" ? "— une personne —" : "— une machine —"}</option>
          {libres.map((r) => (
            <option key={r.ressourceId} value={r.ressourceId}>
              {r.libelle}
              {r.detail === null ? "" : ` · ${r.detail}`}
            </option>
          ))}
        </select>
      </label>
      <label>
        Sur
        <select
          value={quand}
          onChange={(e) => {
            setQuand(e.target.value as "journee" | "matin" | "apres_midi");
          }}
        >
          <option value="journee">la journée</option>
          <option value="matin">le matin seul</option>
          <option value="apres_midi">l&apos;après-midi seul</option>
        </select>
      </label>
      <button type="button" disabled={quoi === ""} onClick={ajouter}>
        Ajouter à la journée
      </button>
      {libres.length === 0 && (
        <span className="saisie-heures__detail">
          Tout le référentiel est déjà à l&apos;écran.
        </span>
      )}
    </div>
  );
}
